import { Form } from "antd"
import { Alert, Legend } from "components/common"
import { useEffect, useMemo, useRef } from "react"
import { t } from "i18next"
import FractionalPaymentItem from "../fractionalPaymentItem"
import {
  FRACTIONAL_PAYMENT_CHANNEL_PSID,
  FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
  paymentModesMapFromResponse,
  resolvePsidPaymentModeKey,
} from "../fractionalPaymentUtils"
import { PAYMENTS_FORM_LIST_NAME } from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/utils/paymentUtils"

export default function FractionalPaymentSection({
  form,
  referenceAmount,
  projectId,
  modeOfPayments,
  loadingPaymentMethods = false,
  paymentApis,
  paymentOptions,
}) {
  const { isRaFlowEnabled, ocrEnabled } = paymentOptions

  const paymentModesMap = useMemo(() => paymentModesMapFromResponse(modeOfPayments), [modeOfPayments])
  const psidPaymentModeKey = useMemo(() => resolvePsidPaymentModeKey(modeOfPayments), [modeOfPayments])

  const coercedProjectId = useMemo(() => {
    if (projectId == null || projectId === "") return null
    const n = +projectId
    return n > 0 ? n : null
  }, [projectId])

  const paymentChannel = Form.useWatch("fractionalPaymentChannel", form) ?? FRACTIONAL_PAYMENT_CHANNEL_STANDARD

  const paymentApisRef = useRef(paymentApis)
  paymentApisRef.current = paymentApis

  const prevCoercedProjectIdRef = useRef(null)

  useEffect(() => {
    if (coercedProjectId == null) return
    const total = +referenceAmount
    if (!Number.isFinite(total) || total <= 0) return
    form.setFieldValue([PAYMENTS_FORM_LIST_NAME, 0, "amount"], Math.round(total))
  }, [coercedProjectId, referenceAmount, form])

  useEffect(() => {
    if (coercedProjectId == null) {
      prevCoercedProjectIdRef.current = null
      return
    }

    const projectChanged = prevCoercedProjectIdRef.current !== coercedProjectId
    prevCoercedProjectIdRef.current = coercedProjectId

    if (projectChanged) {
      form.setFieldValue(PAYMENTS_FORM_LIST_NAME, [{ enableDepositRA: false }])
      form.setFieldValue("fractionalPaymentChannel", FRACTIONAL_PAYMENT_CHANNEL_STANDARD)
    }

    if (!psidPaymentModeKey) return

    const channel = projectChanged ? FRACTIONAL_PAYMENT_CHANNEL_STANDARD : paymentChannel

    const { getPaymentMode, getBankName } = paymentApisRef.current

    if (channel === FRACTIONAL_PAYMENT_CHANNEL_PSID) {
      form.setFieldValue([PAYMENTS_FORM_LIST_NAME, 0, "modeOfPayment"], psidPaymentModeKey)
      const meta = modeOfPayments?.find(m => m.key === psidPaymentModeKey)
      if (meta?.id) {
        getPaymentMode(meta.id, true)
        if (meta?.getBankNames) getBankName({}, true)
      }
      return
    }

    const cur = form.getFieldValue([PAYMENTS_FORM_LIST_NAME, 0, "modeOfPayment"])
    if (cur === psidPaymentModeKey) {
      form.setFieldValue([PAYMENTS_FORM_LIST_NAME, 0, "modeOfPayment"], undefined)
    }
  }, [coercedProjectId, paymentChannel, psidPaymentModeKey, modeOfPayments, form])

  if (coercedProjectId == null) {
    return null
  }

  if (modeOfPayments && !modeOfPayments?.length && !loadingPaymentMethods) {
    return (
      <Alert type="error" message={t("No acceptable payment methods are available in the project configurations")} />
    )
  }

  return (
    <div className="mbs-16">
      <Legend title={t("Add Payment")} style={{ marginTop: 16 }} />

      <Form.List name={PAYMENTS_FORM_LIST_NAME}>
        {fields => {
          if (!fields.length) return null
          const { key, name: listFieldName, ...restField } = fields[0]
          return (
            <FractionalPaymentItem
              key={key}
              listFieldName={listFieldName}
              restField={restField}
              form={form}
              projectId={coercedProjectId}
              modeOfPayments={modeOfPayments}
              paymentModesMap={paymentModesMap}
              psidPaymentModeKey={psidPaymentModeKey}
              paymentApis={paymentApis}
              paymentOptions={{ isRaFlowEnabled, ocrEnabled }}
              paymentChannel={paymentChannel}
            />
          )
        }}
      </Form.List>
    </div>
  )
}
